import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check } from '@mui/icons-material';
import { Loader, MakeToast } from '../../../../../components';
import { NoAccessToLLM } from './data/constants';
import { ragProps } from './rag.props';
import { modelsTypes } from '../../../llm/pages/llm.props';
import { ChatModelContext } from '../../../../../utils/context';
import makeToast from '../../../../../components/toaster';
import Logout from '../../../../../auth/protected/session-expire-modal';
import {
  ADMIN_DISABLED_MODEL_MESSAGE,
  isAdminBlockedModelStatus,
  PREFERRED_OPENAI_CHAT_MODEL_NAME,
} from '../../../../../services';
import { UpdateModelForProvider } from '../../services/chat';
import Searchbar from '../../../../../components/searchbar';
import {
  filterModelsByCapabilities,
  type ChatFeatureContext,
  type ModelWithCapabilities,
} from '../../lib/model-capability-filter';

function Rag({
  dispatch,
  state,
  activeProviderId,
  handleModelProviderTabChange,
  llmPermissions,
  llmPermissionsLoaded,
  mcpActive = false,
}: ragProps) {
  const [models, setModels] = useState<modelsTypes[]>([]);
  const [loader, setLoader] = useState(true);
  const [logout, setLogout] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { chatModel, setChatModel } = useContext(ChatModelContext);

  useEffect(() => {
    const permissions = localStorage.getItem('configurations');
    if (!permissions) {
      setLoader(false);
      return;
    }
    if (!llmPermissionsLoaded) return;
    if (llmPermissions?.length) {
      setModels(llmPermissions);
    } else {
      setModels([]);
    }
    setLoader(false);
  }, [llmPermissions, llmPermissionsLoaded]);

  const data = localStorage.getItem(`chat`);
  const parsedChat = data ? JSON.parse(data) : [];
  const providerChatRow = parsedChat.find(
    (entry: { provider_id?: number }) => entry.provider_id === activeProviderId,
  );
  const featureContext: ChatFeatureContext = {
    thinkingEnabled: Boolean(state.thinkingEnabled),
    webSearchEnabled: Boolean(state.webSearchEnabled ?? providerChatRow?.data?.webSearchEnabled),
    codeInterpreterEnabled: Boolean(providerChatRow?.data?.codeInterpreterEnabled),
    mcpActive,
  };
  const providerModels = models
    .filter((item) => item.proverID === activeProviderId)
    .filter((item) => typeof item !== 'boolean') as ModelWithCapabilities[];

  const usableModels = filterModelsByCapabilities(
    providerModels.filter((item) => !isAdminBlockedModelStatus(item.model_status)),
    featureContext,
  );
  const blockedModels = providerModels.filter((item) =>
    isAdminBlockedModelStatus(item.model_status),
  );
  const listedModels = [...usableModels, ...blockedModels];

  const RagOptions =
    listedModels.flatMap((item) => {
      const { name, id: rawId } = item;
      if (!name) return [];
      const id = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId;
      if (id == null || Number.isNaN(id)) return [];
      return [
        {
          label: name,
          value: name,
          id,
          disabled: isAdminBlockedModelStatus(item.model_status),
          modelStatus: item.model_status,
        },
      ];
    }) ?? [];

  const filteredRagOptions =
    searchQuery.trim() === ''
      ? RagOptions
      : RagOptions.filter(
          (item) =>
            item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.value.toLowerCase().includes(searchQuery.toLowerCase()),
        );

  useEffect(() => {
    setSearchQuery('');
  }, [activeProviderId]);

  useEffect(() => {
    if (RagOptions.length > 0) {
      const existingChat = JSON.parse(localStorage.getItem('chat') || '[]');
      const currentProviderData = existingChat.find(
        (entry: { provider_id: number }) => entry.provider_id === activeProviderId,
      );
      const currentModal = currentProviderData?.data?.modal;

      const activeProviderName =
        models.find((m) => m.proverID === activeProviderId)?.provider_name ?? '';
      const isOpenAi = activeProviderName.toLowerCase() === 'openai';
      const hasProviderInfo = models.some((m) => m.proverID === activeProviderId);
      const matchesDefault = (opt: { value: string; label: string }) => {
        const v = (opt.value ?? '').toLowerCase();
        const l = (opt.label ?? '').toLowerCase();
        const needle = PREFERRED_OPENAI_CHAT_MODEL_NAME.toLowerCase();
        return (
          opt.value === PREFERRED_OPENAI_CHAT_MODEL_NAME ||
          opt.label === PREFERRED_OPENAI_CHAT_MODEL_NAME ||
          v === needle ||
          l === needle ||
          v.includes(needle) ||
          l.includes(needle)
        );
      };

      // Only apply default when user has not selected a model yet. Once they choose a model, respect it.
      // Wait for provider info (so we know if OpenAI) before applying default, so PREFERRED_OPENAI_CHAT_MODEL_NAME is used when applicable.
      const shouldApplyDefault = !currentModal?.name;

      if (!shouldApplyDefault && currentModal?.name) {
        setChatModel(`${currentModal.name}|${currentModal.id}`);
      } else if (shouldApplyDefault && hasProviderInfo) {
        const selectable = RagOptions.filter((opt) => !opt.disabled);
        const defaultPool = selectable.length > 0 ? selectable : RagOptions;
        const defaultOption = isOpenAi
          ? (defaultPool.find((opt) => matchesDefault(opt)) ?? defaultPool[0])
          : defaultPool[0];
        if (!defaultOption) {
          return;
        }

        setChatModel(`${defaultOption.value}|${defaultOption.id}`);
        const hasEntryForProvider = existingChat.some(
          (item: { provider_id: number }) => item.provider_id === activeProviderId,
        );
        const updatedChat =
          existingChat.length > 0 && hasEntryForProvider
            ? existingChat.map(
                (item: { provider_id: number; data: { chatData: []; modal: { name: string } } }) =>
                  item.provider_id === activeProviderId
                    ? {
                        ...item,
                        data: {
                          ...item.data,
                          modal: { id: defaultOption.id, name: defaultOption.value },
                        },
                      }
                    : item,
              )
            : [
                ...existingChat,
                {
                  provider_id: activeProviderId,
                  data: {
                    chatData: [],
                    use_document: false,
                    has_document: false,
                    modal: { id: defaultOption.id, name: defaultOption.value },
                  },
                },
              ];
        localStorage.setItem('chat', JSON.stringify(updatedChat));
      }
    }
  }, [RagOptions, activeProviderId, models]);

  let parsedData:
    | [
        {
          provider_id: number;
          data: {
            use_document: boolean;
            has_document: boolean;
            chatData: Array<object>;
            modal?: { name: string; id: number };
          };
        },
      ]
    | null = null;

  if (data !== null) {
    try {
      parsedData = JSON.parse(data);
    } catch (error) {
      MakeToast('error', 'Failed to parse chat data from localStorage');
    }
  }

  const handleModalChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const [value, id] = e.target.value.split('|');
    // Check if the selected model is already active
    const currentModel = parsedData?.find((entry) => entry.provider_id === activeProviderId)?.data
      ?.modal?.name;
    if (currentModel === value) {
      return; // Exit early if same model is selected
    }
    const history: string | null = localStorage.getItem('chat');
    const parsedHistory: {
      data: { chatData: []; use_document: boolean; modal: { name: string }; file_id: string };
      provider_id: number;
      session_id: string;
    }[] = history ? JSON.parse(history) : [];
    const currentProviderData = parsedHistory.find(
      (entry) => entry.provider_id === activeProviderId,
    );

    const isFirstQuery =
      !currentProviderData ||
      !currentProviderData.data?.chatData ||
      currentProviderData.data.chatData.length === 0;

    let updatedData = [];

    if (currentProviderData?.session_id) {
      UpdateModelForProvider({
        sessionId: currentProviderData?.session_id || '0',
        model: value,
        type: 'Switch-Model',
      })
        .then((res) => {
          if (res.status === 200) {
            if (isFirstQuery && !currentProviderData?.session_id) {
              updatedData = parsedData?.map(
                (entry: {
                  provider_id: number;
                  data: {
                    use_document: boolean;
                    has_document: boolean;
                    chatData: Array<object>;
                    modal?: object;
                  };
                }) =>
                  entry.provider_id === activeProviderId
                    ? {
                        ...entry,
                        data: {
                          ...entry.data,
                          modal: {
                            ...entry.data?.modal,
                            name: value,
                            id: id ? parseInt(id, 10) : 0,
                          },
                        },
                      }
                    : entry,
              ) || [
                {
                  provider_id: activeProviderId,
                  data: {
                    chatData: [],
                    use_document: parsedData?.find(
                      (entry) => entry.provider_id === activeProviderId,
                    )?.data?.use_document,
                    has_document: parsedData?.find(
                      (entry) => entry.provider_id === activeProviderId,
                    )?.data?.has_document,
                    modal: {
                      ...parsedData?.find((entry) => entry.provider_id === activeProviderId)?.data,
                      name: value,
                      id: id ? parseInt(id, 10) : 0,
                    },
                  },
                },
              ];
            } else {
              updatedData = parsedData?.map(
                (entry: {
                  provider_id: number;
                  data: {
                    use_document: boolean;
                    has_document: boolean;
                    chatData: Array<object>;
                    modal?: object;
                  };
                }) =>
                  entry.provider_id === activeProviderId
                    ? {
                        ...entry,
                        data: {
                          ...entry.data,
                          modal: {
                            ...entry.data?.modal,
                            name: value,
                            id: id ? parseInt(id, 10) : 0,
                          },
                          chatData: [
                            ...(entry?.data?.chatData || []),
                            {
                              role: 'Switch-Model',
                              message: `Switched to model: ${value}`,
                              time: new Date(),
                              type: value,
                            },
                          ],
                        },
                      }
                    : entry,
              ) || [
                {
                  provider_id: activeProviderId,
                  data: {
                    chatData: [],
                    use_document: parsedData?.find(
                      (entry) => entry.provider_id === activeProviderId,
                    )?.data?.use_document,
                    has_document: parsedData?.find(
                      (entry) => entry.provider_id === activeProviderId,
                    )?.data?.has_document,
                    modal: {
                      ...parsedData?.find((entry) => entry.provider_id === activeProviderId)?.data,
                      name: value,
                      id: id ? parseInt(id, 10) : 0,
                    },
                  },
                },
              ];
            }
            setChatModel(`${value}|${id}`);
            localStorage.setItem('chat', JSON.stringify(updatedData));
            dispatch({
              type: 'SET_CHAT_HISTORY',
              payload:
                updatedData?.find((entry) => entry.provider_id === activeProviderId)?.data
                  ?.chatData || [],
            });
          }
        })
        .catch((err) => {
          if (err?.response?.status === 401) {
            setLogout(true);
          } else {
            makeToast('error', err?.response?.data?.message || 'Error updating model');
          }
        });
    } else {
      updatedData = parsedData?.map(
        (entry: {
          provider_id: number;
          data: {
            use_document: boolean;
            has_document: boolean;
            chatData: Array<object>;
            modal?: object;
          };
        }) =>
          entry.provider_id === activeProviderId
            ? {
                ...entry,
                data: {
                  ...entry.data,
                  modal: { ...entry.data?.modal, name: value, id: id ? parseInt(id, 10) : 0 },
                },
              }
            : entry,
      ) || [
        {
          provider_id: activeProviderId,
          data: {
            chatData: [],
            use_document: parsedData?.find((entry) => entry.provider_id === activeProviderId)?.data
              ?.use_document,
            has_document: parsedData?.find((entry) => entry.provider_id === activeProviderId)?.data
              ?.has_document,
            modal: {
              ...parsedData?.find((entry) => entry.provider_id === activeProviderId)?.data,
              name: value,
              id: id ? parseInt(id, 10) : 0,
            },
          },
        },
      ];
      setChatModel(`${value}|${id}`);
      localStorage.setItem('chat', JSON.stringify(updatedData));
    }
  };

  function renderRagContent() {
    if (RagOptions?.length > 0 && !loader) {
      const sortedOptions = [...filteredRagOptions].sort((a, b) => a.value.localeCompare(b.value));
      return (
        <div className="flex flex-col gap-1.5">
          <Searchbar
            placeholder="Search models"
            variant="compact"
            value={searchQuery}
            handleSearchByChange={(value: string) => setSearchQuery(value)}
          />
          <div className="Models flex flex-col gap-0.5 overflow-y-auto max-h-[300px] invisible-scroller">
            {sortedOptions.length > 0 ? (
              sortedOptions.map((item) => {
                const selected = chatModel === `${item.value}|${item.id}`;
                const blocked = Boolean(item.disabled);
                let statusLabel = 'Disabled';
                if (String(item.modelStatus || '').toLowerCase() === 'deprecated') {
                  statusLabel = 'Deprecated';
                }
                let rowClass = 'flex items-center gap-2 px-3 py-2 rounded-xl transition-colors ';
                if (blocked && selected) {
                  rowClass += 'cursor-not-allowed text-zinc-400 bg-zinc-100';
                } else if (blocked) {
                  rowClass += 'cursor-not-allowed text-zinc-400';
                } else if (selected) {
                  rowClass += 'cursor-pointer bg-[#e0f1f8] text-[#0A8BC7]';
                } else {
                  rowClass += 'cursor-pointer text-zinc-800 hover:bg-zinc-50';
                }
                return (
                  <div
                    key={`${item.value}|${item.id}`}
                    className={rowClass}
                    role="button"
                    tabIndex={blocked ? -1 : 0}
                    aria-disabled={blocked}
                    onClick={() => {
                      if (blocked) {
                        MakeToast('error', ADMIN_DISABLED_MODEL_MESSAGE);
                        return;
                      }
                      const syntheticEvent = {
                        target: { value: `${item.value}|${item.id}` },
                      } as React.ChangeEvent<HTMLSelectElement>;
                      handleModalChange(syntheticEvent);
                      handleModelProviderTabChange?.(false);
                    }}
                    onKeyDown={(e) => {
                      if (blocked) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        const syntheticEvent = {
                          target: { value: `${item.value}|${item.id}` },
                        } as React.ChangeEvent<HTMLSelectElement>;
                        handleModalChange(syntheticEvent);
                      }
                    }}
                  >
                    <span className="flex-1 text-sm font-medium truncate">{item.label}</span>
                    {blocked ? (
                      <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">
                        {statusLabel}
                      </span>
                    ) : null}
                    {selected && !blocked ? <Check sx={{ fontSize: 18 }} /> : null}
                  </div>
                );
              })
            ) : (
              <div className="px-3 py-4 text-center text-sm text-zinc-500">No results found</div>
            )}
          </div>
        </div>
      );
    }
    if (loader) {
      return <Loader color="white" />;
    }
    return <div className="bg-[#EF7707] w-full pl-3">{NoAccessToLLM}</div>;
  }

  return (
    <>
      {renderRagContent()}
      {logout && createPortal(<Logout />, document.body)}
    </>
  );
}

export default Rag;
